import { RefObject, useCallback, useLayoutEffect, useRef, useState } from 'react';
import {
	COMPRESSED_OFF_PX,
	COMPRESSED_ON_PX,
	computeColumnLayout,
	OVERFLOW_THRESHOLD_PX,
	type LayoutMode,
} from './column-width-functions';
import {
	computeScrollThumbStyle,
	EDGE_SHADOW_OFF_PX,
	EDGE_SHADOW_ON_PX,
	getHorizontalScrollProgress,
	WHEEL_EDGE_RELEASE_PX,
} from './scroll-track';
import {
	getRowLayoutWidth,
	getVisibleColumnIndices,
	mapWidthsToAllColumns,
	measureNaturalColumnWidths,
	pickNaturalWidthsByIndices,
} from './table-measure';

const RESIZE_DEBOUNCE_MS = 50;
const SCROLL_IDLE_MS = 150;

interface ScrollThumbStyle {
	width: string;
	transform: string;
}

export interface TableColumnLayoutResult {
	columnWidths: (number | undefined)[] | null;
	layoutMode: LayoutMode;
	isHorizontalOverflow: boolean;
	isCompressed: boolean;
	isStickyScrolled: boolean;
	thumbStyle: ScrollThumbStyle;
}

export default function useTableColumnLayout(
	tableRef: RefObject<HTMLDivElement | null>,
	scrollRef: RefObject<HTMLDivElement | null>,
	trackRef: RefObject<HTMLDivElement | null>,
	dependencyKey: string,
	stickyRowHeader = false
): TableColumnLayoutResult {
	const [columnWidths, setColumnWidths] = useState<(number | undefined)[] | null>(null);
	const [layoutMode, setLayoutMode] = useState<LayoutMode>('fill');
	const [isHorizontalOverflow, setIsHorizontalOverflow] = useState(false);
	const [isCompressed, setIsCompressed] = useState(false);
	const [isStickyScrolled, setIsStickyScrolled] = useState(false);
	const [thumbStyle, setThumbStyle] = useState<ScrollThumbStyle>({
		width: '100%',
		transform: 'translateX(0)',
	});

	const isCompressedRef = useRef(false);
	const stickyScrolledRef = useRef(false);
	const needsMeasureRef = useRef(true);
	const isScrollingRef = useRef(false);
	const scrollIdleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const resizeDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const markScrolling = useCallback(() => {
		isScrollingRef.current = true;

		if (scrollIdleTimerRef.current != null) {
			clearTimeout(scrollIdleTimerRef.current);
		}

		scrollIdleTimerRef.current = setTimeout(() => {
			isScrollingRef.current = false;
			scrollIdleTimerRef.current = null;
		}, SCROLL_IDLE_MS);
	}, []);

	const syncStickyScrollState = useCallback(
		(scrollEl: HTMLElement, overflow: boolean) => {
			if (!stickyRowHeader || !overflow) {
				if (stickyScrolledRef.current) {
					stickyScrolledRef.current = false;
					setIsStickyScrolled(false);
				}
				return;
			}

			const maxScrollPx = scrollEl.scrollWidth - scrollEl.clientWidth;

			// If the maximum scroll width is less than or equal to the overflow threshold, set the sticky scrolled state to false
			if (maxScrollPx <= OVERFLOW_THRESHOLD_PX) {
				if (stickyScrolledRef.current) {
					stickyScrolledRef.current = false;
					setIsStickyScrolled(false);
				}
				return;
			}

			const scrolledPx = getHorizontalScrollProgress(scrollEl) * maxScrollPx;
			const shadowOn = stickyScrolledRef.current;

			// If the shadow is not on and the scrolled pixel is greater than the edge shadow on pixel, set the sticky scrolled state to true
			if (!shadowOn && scrolledPx > EDGE_SHADOW_ON_PX) {
				stickyScrolledRef.current = true;
				setIsStickyScrolled(true);
			} else if (shadowOn && scrolledPx < EDGE_SHADOW_OFF_PX) {
				// If the shadow is on and the scrolled pixel is less than the edge shadow off pixel, set the sticky scrolled state to false
				stickyScrolledRef.current = false;
				setIsStickyScrolled(false);
			}
		},
		[stickyRowHeader]
	);

	const syncScrollTrack = useCallback(
		(scrollEl: HTMLElement) => {
			const trackEl = trackRef.current;
			if (!trackEl) return false;

			const nextThumb = computeScrollThumbStyle(scrollEl, trackEl.clientWidth);
			setThumbStyle({ width: nextThumb.width, transform: nextThumb.transform });
			setIsHorizontalOverflow(nextThumb.visible);
			syncStickyScrollState(scrollEl, nextThumb.visible);
			return nextThumb.visible;
		},
		[trackRef, syncStickyScrollState]
	);

	const schedulePostLayoutSync = useCallback(
		(scrollEl: HTMLElement, mode: LayoutMode) => {
			// After paint, refine compressed state and sync scroll track
			requestAnimationFrame(() => {
				const overflowPx = scrollEl.scrollWidth - scrollEl.clientWidth;
				// If the layout mode is scroll, refine the compressed state
				if (mode === 'scroll') {
					// If the compressed state is true, use the compressed off pixel
					const useCompressed = isCompressedRef.current
						? overflowPx > COMPRESSED_OFF_PX
						: overflowPx > COMPRESSED_ON_PX;
					isCompressedRef.current = useCompressed;
					setIsCompressed(useCompressed);
				}

				syncScrollTrack(scrollEl);
			});
		},
		[syncScrollTrack]
	);

	const applyMeasuredLayout = useCallback(
		(table: HTMLElement, scrollEl: HTMLElement) => {
			const naturalWidths = measureNaturalColumnWidths(table);
			const visibleIndices = getVisibleColumnIndices(table);
			const visibleNaturals = pickNaturalWidthsByIndices(
				naturalWidths,
				visibleIndices.length > 0 ? visibleIndices : naturalWidths.map((_, index) => index)
			);
			const layoutWidth = getRowLayoutWidth(table, scrollEl);
			// Compute declarative widths (fill or scroll)
			const layout = computeColumnLayout(layoutWidth, visibleNaturals);
			const indices =
				visibleIndices.length > 0 ? visibleIndices : naturalWidths.map((_, index) => index);
			const fullColumnWidths = mapWidthsToAllColumns(naturalWidths.length, indices, layout.widths);

			const shouldCompress = layout.mode === 'scroll';
			isCompressedRef.current = shouldCompress;

			setColumnWidths(fullColumnWidths);
			setLayoutMode(layout.mode);
			setIsCompressed(shouldCompress);
			needsMeasureRef.current = false;

			schedulePostLayoutSync(scrollEl, layout.mode);
		},
		[schedulePostLayoutSync]
	);

	const requestMeasure = useCallback(() => {
		needsMeasureRef.current = true;
		setColumnWidths(null);
	}, []);

	// Invalidate widths when row content affecting layout changes.
	useLayoutEffect(() => {
		requestMeasure();
	}, [dependencyKey, requestMeasure]);

	// Measure only after React has committed without fixed column widths.
	useLayoutEffect(() => {
		if (columnWidths !== null || !needsMeasureRef.current) {
			return;
		}

		const table = tableRef.current;
		const scrollEl = scrollRef.current;
		if (!table || !scrollEl) {
			return;
		}

		applyMeasuredLayout(table, scrollEl);
	}, [columnWidths, applyMeasuredLayout, tableRef, scrollRef]);

	// Re-measure on container resize (debounced; skipped while the user is scrolling).
	useLayoutEffect(() => {
		if (columnWidths === null) {
			return;
		}

		const scrollEl = scrollRef.current;
		if (!scrollEl) {
			return;
		}

		const observer = new ResizeObserver(() => {
			if (needsMeasureRef.current || isScrollingRef.current) {
				return;
			}

			if (resizeDebounceTimerRef.current != null) {
				clearTimeout(resizeDebounceTimerRef.current);
			}

			resizeDebounceTimerRef.current = setTimeout(() => {
				resizeDebounceTimerRef.current = null;
				if (!isScrollingRef.current && !needsMeasureRef.current) {
					requestMeasure();
				}
			}, RESIZE_DEBOUNCE_MS);
		});

		observer.observe(scrollEl);

		return () => {
			observer.disconnect();
			if (resizeDebounceTimerRef.current != null) {
				clearTimeout(resizeDebounceTimerRef.current);
				resizeDebounceTimerRef.current = null;
			}
		};
	}, [columnWidths, requestMeasure, scrollRef]);

	// Map vertical wheel to horizontal scroll when the table overflows.
	useLayoutEffect(() => {
		const scrollEl = scrollRef.current;
		if (!scrollEl || columnWidths === null) return;

		let rafId: number | null = null;
		const scheduleSync = () => {
			markScrolling();
			if (rafId != null) return;
			rafId = requestAnimationFrame(() => {
				rafId = null;
				syncScrollTrack(scrollEl);
			});
		};

		const onWheelHorizontal = (event: WheelEvent) => {
			if (!isHorizontalOverflow) return;

			const maxScroll = scrollEl.scrollWidth - scrollEl.clientWidth;
			if (maxScroll <= OVERFLOW_THRESHOLD_PX) return;
			if (Math.abs(event.deltaX) > Math.abs(event.deltaY) || event.ctrlKey) return;

			const delta = event.deltaY + (event.deltaZ || 0);
			if (delta === 0) return;

			const scrolledPx = getHorizontalScrollProgress(scrollEl) * maxScroll;
			if (delta > 0 && scrolledPx >= maxScroll - WHEEL_EDGE_RELEASE_PX) return;
			if (delta < 0 && scrolledPx <= WHEEL_EDGE_RELEASE_PX) return;

			event.preventDefault();
			scrollEl.scrollBy({ left: delta, behavior: 'instant' });
			scheduleSync();
		};

		const onScroll = () => {
			scheduleSync();
		};

		scrollEl.addEventListener('scroll', onScroll, { passive: true });
		scrollEl.addEventListener('wheel', onWheelHorizontal, { passive: false });

		const trackEl = trackRef.current;
		trackEl?.addEventListener('wheel', onWheelHorizontal, { passive: false });

		return () => {
			scrollEl.removeEventListener('scroll', onScroll);
			scrollEl.removeEventListener('wheel', onWheelHorizontal);
			trackEl?.removeEventListener('wheel', onWheelHorizontal);
			if (rafId != null) cancelAnimationFrame(rafId);
			if (scrollIdleTimerRef.current != null) {
				clearTimeout(scrollIdleTimerRef.current);
				scrollIdleTimerRef.current = null;
			}
		};
	}, [
		scrollRef,
		trackRef,
		syncScrollTrack,
		markScrolling,
		columnWidths,
		dependencyKey,
		isHorizontalOverflow,
	]);

	useLayoutEffect(() => {
		if (!stickyRowHeader || !isHorizontalOverflow) {
			stickyScrolledRef.current = false;
			setIsStickyScrolled(false);
		}
	}, [stickyRowHeader, isHorizontalOverflow]);

	return {
		columnWidths,
		layoutMode,
		isHorizontalOverflow,
		isCompressed,
		isStickyScrolled,
		thumbStyle,
	};
}
